import Button from 'components/base/Button'
import Link from 'components/base/Link'
import PageMeta from 'components/base/PageMeta'
import SchemaMarkup from 'components/base/SchemaMarkup'
import Container from 'components/landing/Container'
import Header from 'components/landing/Header'
import DemoContactForm from 'components/landing/sellematics/blocks/DemoContactForm'
import Faq, { FaqItem } from 'components/landing/sellmonitor/blocks/Faq'
import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { getCanonicalHref } from 'lib/canonical'
import { getFAQPageSchema } from 'lib/schema-utils'
import { useIntl } from 'lib/intl-utils'

const contentByLang = {
  ru: {
    bulk_heading: 'Массовая настройка товаров',
    bulk_description: 'Меняйте настройки сразу у группы товаров — в интерфейсе или через Excel. Объединяйте товары тегами и обновляйте только нужные параметры, сохраняя остальные настройки.',
    scenario_cross_marketplace_title: 'Сценарий «Кросс-маркетплейс»',
    scenario_cross_marketplace_description: 'Скорректируем цену относительно цены вашего товара на другой площадке. Например, будем удерживать цену на Ozon на том же уровне, что и на Wildberries.',
    protection_margin_title: 'Минимальная маржинальность',
    protection_margin_description: 'Не снижаем цену, если маржинальность окажется ниже заданного вами минимума',
    demo_description: 'Проведём демонстрацию инструмента, покажем сценарии для вашего ассортимента и предоставим демо-доступ.',
    demo_heading: 'Попробуйте управление ценами',
    faq_bulk_answer: 'Вы можете выбрать несколько товаров и изменить настройки вместе или воспользоваться Excel: скачать шаблон, заполнить параметры и загрузить таблицу обратно. На демонстрации покажем, как настроить управление для вашего ассортимента.',
    faq_bulk_question: 'Как настроить сразу много товаров?',
    faq_heading: 'Часто задаваемые вопросы',
    faq_multiple_answer: 'Да. Репрайсер применяет первый подходящий сценарий по порядку. Сценарии по конкурентам, расписанию, остаткам и кросс-маркетплейс можно переставлять. Расчёт по маржинальности идёт перед фиксированной ценой; фиксированная цена используется как базовый сценарий.',
    faq_multiple_question: 'Можно ли настроить несколько сценариев для одного товара?',
    faq_price_answer: 'В настройках товара вы выбираете, какую цену удерживать: со скидкой продавца, со скидкой маркетплейса или со скидкой за оплату WB Кошельком или Ozon Картой. На Ozon управление ценой со скидкой маркетплейса или Ozon Карты доступно при автоматическом выходе из акций.',
    faq_price_question: 'Какой ценой можно управлять?',
    faq_promotions_answer: 'Вы задаёте допустимое снижение от цены, рассчитанной по сценариям. Репрайсер выбирает акции, которые укладываются в это ограничение. Можно стремиться попасть во все доступные акции или выбрать одну с самой высокой ценой участия. На Ozon также доступен выбор акции с максимальным бустингом и автоматический выход из всех акций.',
    faq_promotions_question: 'Как выбираются акции для участия?',
    faq_rrp_answer: 'Укажите рекомендованную розничную цену в сценарии «Фиксированная цена» и выберите, какой тип цены нужно контролировать. Репрайсер будет проверять её и корректировать по вашим настройкам. Важно заранее определить, к какой цене относится требование производителя: со скидкой продавца, маркетплейса или кошелька.',
    faq_rrp_question: 'Как использовать управление ценами для контроля РРЦ?',
    faq_timing_answer: 'Репрайсер обычно проверяет условия каждую минуту. Частоту самих изменений можно ограничить, а перед срабатыванием правила — задать задержку. Обновление цены на витрине также зависит от обработки изменений маркетплейсом.',
    faq_timing_question: 'Проверка раз в минуту означает, что цена изменится за минуту?',
    hero_cta: 'Запросить демо',
    hero_description: 'Автоматически управляйте ценами на маркетплейсах, контролируйте соблюдение РРЦ, учитывайте цены конкурентов, условия акций, остатки и маржинальность — без постоянной ручной проверки цен.',
    hero_marketplaces: 'Wildberries и Ozon',
    hero_title: 'Управление ценами на',
    intro_actual_price_description: 'Следим за ценой со всеми скидками маркетплейса, которую видит и оплачивает покупатель',
    intro_actual_price_title: 'Контролируем реальную цену',
    intro_assortment_description: 'Вручную управлять тысячами товаров невозможно. Мы делаем это за вас 24 / 7',
    intro_assortment_title: 'Управляем тысячами товаров',
    intro_description: 'Как продавец, в личном кабинете маркетплейса вы можете менять у товара цену без скидки и со скидкой. Но покупатели видят не эту цену, а цену с дополнительной скидкой от маркетплейса, которая может меняться.',
    intro_explanation: 'Управление ценами Sellematics, или репрайсер, будет контролировать за вас реальную цену, которую видят покупатели.',
    intro_heading: 'Что такое управление ценами?',
    intro_minute_description: 'Проверяем каждый товар каждую минуту. Сразу же реагируем на любые отклонения и исправляем их',
    intro_minute_title: 'Мониторим цены раз в минуту',
    meta_description: 'Репрайсер Sellematics проверяет цены каждую минуту и меняет их по вашим правилам: с учётом маржинальности, конкурентов, остатков, расписания и акций. Получите демонстрацию и демо-доступ.',
    meta_title: 'Управление ценами на Wildberries и Ozon — репрайсер Sellematics',
    needs_assortment_description: 'Задайте правила для ассортимента — репрайсер возьмёт рутинный контроль цен на себя и сократит время на работу с товарами.',
    needs_assortment_title: 'У вас большая товарная матрица?',
    needs_margin_description: 'Рассчитаем цену для заданной маржинальности с учётом расходов маркетплейса, налогов и себестоимости товара.',
    needs_margin_title: 'Замечаете, что периодически товары работают в минус?',
    needs_promotions_description: 'Автоматически управляем участием товаров в акциях: учитываем условия маркетплейса и заданные вами правила.',
    needs_promotions_title: 'Акции появляются так часто, что сложно следить за условиями и понимать, где участвовать?',
    needs_rrp_description: 'Будем контролировать соответствие цен на маркетплейсах требованиям производителя и корректировать их по заданным правилам.',
    needs_rrp_title: 'Вы дистрибьютор и получаете штрафы за несоответствие РРЦ?',
    protection_calculation_title: 'Защита от ошибок в расчётах',
    protection_conclusion: 'Если проверка не пройдена, цена не меняется — лучше пропустить изменение, чем поставить неправильную цену.',
    protection_data_description: 'Отбрасываем пустые и явно неверные значения до расчёта.',
    protection_data_title: 'Фильтр ошибочных данных',
    protection_description: 'Прежде чем отправить цену на маркетплейс, репрайсер проверяет настройки, исходные данные и результат расчёта.',
    protection_final_description: 'Перед отправкой каждая цена проходит обязательный контроль.',
    protection_final_title: 'Финальная проверка',
    protection_heading: '🛡️ Цены под тройной защитой',
    protection_logic_description: 'Не опираемся на цены, которые не проходят проверку.',
    protection_logic_title: 'Проверка логики цен',
    protection_marketplace_title: 'Защита от сбоев маркетплейса',
    protection_minimum_description: 'Не опускаем цену ниже заданного вами минимума.',
    protection_minimum_title: 'Минимальная цена',
    protection_settings_title: 'Защита от ошибок в настройках',
    protection_steps_description: 'Каждый этап расчёта перепроверяет предыдущий.',
    protection_steps_title: 'Контроль на каждом шаге',
    protection_storefront_description: 'Сверяем данные маркетплейса с ценой на витрине.',
    protection_storefront_title: 'Сверка с витриной',
    protection_threshold_description: 'Дополнительная проверка защищает от слишком низкой цены, даже если минимум указан по ошибке.',
    protection_threshold_title: 'Страховочный порог',
    protection_validation_description: 'Проверяем параметры перед сохранением, чтобы выявить ошибки и противоречия.',
    protection_validation_title: 'Проверка настроек',
    scenario_competitors_description: 'Скорректируем цены ваших товаров относительно цены товаров конкурентов. Например, будем удерживать цену ниже всех конкурентов, чтобы покупатели выбирали ваш товар.',
    scenario_competitors_title: 'Сценарий «По конкурентам»',
    scenario_fixed_description: 'Удержим заданную цену, даже если включится автоакция или поменяется размер скидки. Каждую минуту проверяем цены, если выбранные товары попадают в автоакцию или изменилась скидка маркетплейса, сразу корректируем цену. Так покупатели всегда будут видеть указанную вами цену.',
    scenario_fixed_title: 'Сценарий «Фиксированная цена»',
    scenario_margin_description: 'Рассчитаем цену так, чтобы товар продавался с указанной маржинальностью. Увеличилась комиссия или расходы на логистику? Повысим цену. Уменьшился процент СПП или соинвеста? Изменим цену так, чтобы вы продолжали зарабатывать. Учитываем реальные расходы с маркетплейса, налоги и себестоимость товаров.',
    scenario_margin_title: 'Сценарий «По маржинальности»',
    scenario_promotions_description: 'Подберём акции в пределах допустимого снижения цены. Например, на Ozon выберем акцию с максимальным бустингом для продвижения товара в выдаче.',
    scenario_promotions_title: 'Управление акциями',
    scenario_rounding_description: 'Автоматически применим указанное окончание ко всем ценам. Например, все цены будут заканчиваться на 99.',
    scenario_rounding_title: 'Красивая цена',
    scenario_schedule_description: 'Применим указанную цену в выбранные часы и дни недели. Например, будем повышать цену в будние дни после 18 часов, когда покупатели более активны.',
    scenario_schedule_title: 'Сценарий «По расписанию»',
    scenario_stock_description: 'Поменяем цену в зависимости от количества товара на складе. Например, если товар заканчивается, повысим цену, чтобы его не раскупили до новой поставки.',
    scenario_stock_title: 'Сценарий «По остаткам»',
    scenarios_description: 'Сценарий — это правило, по которому сервис обновляет цену. Можно настроить несколько сценариев и указать порядок. Применяется первый подходящий по порядку сценарий.',
    scenarios_heading: 'Сценарии и настройки управления ценами',
    speed_competitors_description: 'Заметим, что конкуренты снизили цену, и оперативно скорректируем цены на ваши товары, чтобы они всегда были дешевле',
    speed_competitors_title: 'Цены товаров конкурентов',
    speed_description: 'Проверяем цены ваших товаров каждую минуту — намного чаще, чем другие сервисы.',
    speed_discounts_description: 'Любое отклонение от заданной цены, включение автоакции или изменение скидки маркетплейса, исправим за минуту, чтобы не продавать товар по низкой цене',
    speed_discounts_title: 'Автоакция и скидка маркетплейса',
    speed_heading: '⚡ Управление ценами Sellematics быстрее всех',
    speed_settings_description: 'Новые настройки и сценарии управления ценами начнут работать уже через минуту, чтобы вы смогли сразу начать проверять новую стратегию',
    speed_settings_title: 'Изменение настроек',
    speed_stock_description: 'Быстро узнаем об изменении остатка на складе и сразу же повысим цены на ваши товары, чтобы их не раскупили до новой поставки',
    speed_stock_title: 'Остатки на складе',
    trust_ecosystem_description: 'Цены, реклама, аналитика и другие инструменты — в единой системе.',
    trust_ecosystem_title: 'Экосистема\nсервисов',
    trust_heading: 'Почему нам доверяют?',
    trust_minute_description: 'Быстро реагируйте на изменения цен конкурентов и условий маркетплейсов.',
    trust_minute_title: 'Проверка цен\nкаждую минуту',
    trust_rules_description: 'Управляйте ценами с учётом маржинальности, конкурентов, остатков и расписания.',
    trust_rules_title: 'Гибкие правила\nценообразования',
    trust_scenarios_description: 'Настраивайте сценарии под разные задачи и стратегии продаж.',
    trust_scenarios_title: 'Сценарии\nпод ваши задачи',
  },
  en: {
    bulk_heading: 'Bulk product configuration',
    bulk_description: 'Update settings for a group of products in the interface or through Excel. Group products with tags and change only the parameters you need, keeping other settings unchanged.',
    scenario_cross_marketplace_title: '“Cross-marketplace” scenario',
    scenario_cross_marketplace_description: 'We adjust the price relative to your product’s price on the other marketplace. For example, we can keep the price on Ozon at the same level as on Wildberries.',
    protection_margin_title: 'Minimum margin',
    protection_margin_description: 'We do not lower the price if it would bring the margin below your minimum',
    demo_description: 'We will demonstrate the tool, show scenarios for your assortment, and provide trial access.',
    demo_heading: 'Try price management',
    faq_bulk_answer: 'Select several products and update their settings together, or use Excel: download the template, fill in the parameters, and upload it back. During the demo, we will show you how to configure price management for your assortment.',
    faq_bulk_question: 'How can I configure many products at once?',
    faq_heading: 'Frequently asked questions',
    faq_multiple_answer: 'Yes. The repricer applies the first scenario whose conditions are met. You can reorder the competitor, schedule, stock, and cross-marketplace scenarios. Margin-based calculations run before the fixed-price scenario, which serves as the baseline.',
    faq_multiple_question: 'Can I set up several scenarios for one product?',
    faq_price_answer: 'In the product settings, choose which price to maintain: the price after the seller discount, the marketplace discount, or the discount for paying with WB Wallet or Ozon Card. On Ozon, managing the price after marketplace or Ozon Card discounts is available when automatic withdrawal from promotions is enabled.',
    faq_price_question: 'Which price can I manage?',
    faq_promotions_answer: 'You set the maximum allowed reduction from the price calculated by your scenarios. The repricer selects promotions within that limit. You can aim to join all available promotions or choose the one with the highest participation price. On Ozon, you can also choose the promotion with the largest visibility boost or automatically withdraw from all promotions.',
    faq_promotions_question: 'How are promotions selected?',
    faq_rrp_answer: 'Enter the recommended retail price (RRP) in the “Fixed price” scenario and choose which price type to control. The repricer will check and adjust it according to your settings. First, clarify which price the manufacturer requires you to maintain: the price after the seller, marketplace, or wallet discount.',
    faq_rrp_question: 'How can I use price management to maintain the recommended retail price?',
    faq_timing_answer: 'The repricer usually checks conditions every minute. You can limit how often prices change and set a delay before a rule takes effect. Updates to the price shown on the storefront also depend on how quickly the marketplace processes changes.',
    faq_timing_question: 'Does checking every minute mean the price will change within a minute?',
    hero_cta: 'Request a demo',
    hero_description: 'Automatically manage marketplace prices, maintain recommended retail prices, and account for competitor prices, promotion terms, stock levels, and margins — without constant manual price checks.',
    hero_marketplaces: 'Wildberries and Ozon',
    hero_title: 'Price management on',
    intro_actual_price_description: 'We track the price with all marketplace discounts that the customer sees and pays',
    intro_actual_price_title: 'We control the actual price',
    intro_assortment_description: 'Managing thousands of products manually is impossible. We do it for you 24/7',
    intro_assortment_title: 'We manage thousands of products',
    intro_description: 'As a seller, you can change a product’s regular and discounted prices in your marketplace account. But customers see a different price, with an additional marketplace discount that can change.',
    intro_explanation: 'Sellematics price management, also known as a repricer, controls the actual price customers see on your behalf.',
    intro_heading: 'What is price management?',
    intro_minute_description: 'We check every product every minute, immediately respond to deviations, and correct them',
    intro_minute_title: 'We check prices every minute',
    meta_description: 'The Sellematics repricer checks prices every minute and adjusts them according to your rules, taking margins, competitors, stock levels, schedules, and promotions into account. Request a demo and trial access.',
    meta_title: 'Price management on Wildberries and Ozon — Sellematics repricer',
    needs_assortment_description: 'Set rules for your assortment — the repricer will handle routine price checks and reduce the time spent managing products.',
    needs_assortment_title: 'Do you have a large product assortment?',
    needs_margin_description: 'We calculate the price for your target margin, taking marketplace costs, taxes, and product costs into account.',
    needs_margin_title: 'Do you notice that some products occasionally sell at a loss?',
    needs_promotions_description: 'We automatically manage participation in promotions, taking marketplace terms and your rules into account.',
    needs_promotions_title: 'Do promotions appear so often that it is hard to track their terms and decide which ones to join?',
    needs_rrp_description: 'We monitor marketplace prices for compliance with the manufacturer’s requirements and adjust them according to your rules.',
    needs_rrp_title: 'Are you a distributor facing penalties for not maintaining the RRP?',
    protection_calculation_title: 'Protection against calculation errors',
    protection_conclusion: 'If a check fails, the price stays unchanged — it is better to skip an update than set an incorrect price.',
    protection_data_description: 'We discard empty and clearly incorrect values before calculating prices.',
    protection_data_title: 'Invalid data filtering',
    protection_description: 'Before sending a price to the marketplace, the repricer checks the settings, source data, and calculation result.',
    protection_final_description: 'Every price passes a mandatory check before it is sent to the marketplace.',
    protection_final_title: 'Final verification',
    protection_heading: '🛡️ Three layers of price protection',
    protection_logic_description: 'We do not rely on prices that fail validation.',
    protection_logic_title: 'Price consistency checks',
    protection_marketplace_title: 'Protection against marketplace failures',
    protection_minimum_description: 'We never lower the price below the minimum you set.',
    protection_minimum_title: 'Minimum price',
    protection_settings_title: 'Protection against settings errors',
    protection_steps_description: 'Each calculation step verifies the previous one.',
    protection_steps_title: 'Checks at every step',
    protection_storefront_description: 'We compare marketplace data with the price shown on the storefront.',
    protection_storefront_title: 'Storefront verification',
    protection_threshold_description: 'An additional check prevents excessively low prices, even if the minimum was entered incorrectly.',
    protection_threshold_title: 'Safety threshold',
    protection_validation_description: 'We check parameters before saving them to identify errors and conflicts.',
    protection_validation_title: 'Settings validation',
    scenario_competitors_description: 'We adjust your product prices relative to competitor products. For example, we can keep your price below all selected competitors so customers choose your product.',
    scenario_competitors_title: '“Competitors” scenario',
    scenario_fixed_description: 'We maintain your target price even if an automatic promotion starts or a discount changes. We check prices every minute and immediately adjust them if selected products enter an automatic promotion or the marketplace discount changes. Customers will keep seeing the price you set.',
    scenario_fixed_title: '“Fixed price” scenario',
    scenario_margin_description: 'We calculate the price to achieve your target margin. Have commissions or logistics costs increased? We will raise the price. Has the marketplace-funded discount decreased, such as Wildberries’ loyalty discount or Ozon’s co-investment discount? We will adjust the price so you keep earning. We account for actual marketplace costs, taxes, and product costs.',
    scenario_margin_title: '“Target margin” scenario',
    scenario_promotions_description: 'We select promotions within your allowed price reduction. For example, on Ozon we can choose the promotion with the largest visibility boost to promote your product in search results.',
    scenario_promotions_title: 'Promotion management',
    scenario_rounding_description: 'We automatically apply your chosen ending to all prices. For example, every price can end in 99.',
    scenario_rounding_title: 'Price endings',
    scenario_schedule_description: 'We apply your chosen price at specific hours and on specific days of the week. For example, we can raise the price after 6 pm on weekdays, when customers are more active.',
    scenario_schedule_title: '“Schedule” scenario',
    scenario_stock_description: 'We change the price based on the quantity in stock. For example, if a product is running low, we raise its price so it does not sell out before the next delivery.',
    scenario_stock_title: '“Stock levels” scenario',
    scenarios_description: 'A scenario is a rule the service uses to update a price. You can set up several scenarios and choose their order. The first scenario whose conditions are met is applied.',
    scenarios_heading: 'Pricing scenarios and settings',
    speed_competitors_description: 'We detect when competitors lower their prices and promptly adjust yours to keep them lower',
    speed_competitors_title: 'Competitor prices',
    speed_description: 'We check your product prices every minute — much more often than other services.',
    speed_discounts_description: 'We correct deviations from your target price, automatic promotions, or changes in marketplace discounts within a minute, so you do not sell at a low price',
    speed_discounts_title: 'Automatic promotions and discounts',
    speed_heading: '⚡ Sellematics price management is the fastest',
    speed_settings_description: 'New settings and pricing scenarios take effect within a minute, so you can start testing a new strategy straight away',
    speed_settings_title: 'Settings changes',
    speed_stock_description: 'We quickly detect stock changes and raise your product prices so they do not sell out before the next delivery',
    speed_stock_title: 'Stock levels',
    trust_ecosystem_description: 'Pricing, advertising, analytics, and other tools in a single system.',
    trust_ecosystem_title: 'One connected\necosystem',
    trust_heading: 'Why choose us?',
    trust_minute_description: 'Respond quickly to changes in competitor prices and marketplace conditions.',
    trust_minute_title: 'Price checks\nevery minute',
    trust_rules_description: 'Manage prices based on margins, competitors, stock levels, and schedules.',
    trust_rules_title: 'Flexible\npricing rules',
    trust_scenarios_description: 'Configure scenarios for different goals and sales strategies.',
    trust_scenarios_title: 'Scenarios\nfor your goals',
  },
} as const

const RepricerLandingPage: FC = () => {
  const ctx = useUserContext()
  const { lang } = useIntl()
  const content = contentByLang[lang === 'en' ? 'en' : 'ru']

  const faqs: FaqItem[] = [
    {
      question: content.faq_price_question,
      answer: content.faq_price_answer,
    },
    {
      question: content.faq_rrp_question,
      answer: content.faq_rrp_answer,
    },
    {
      question: content.faq_multiple_question,
      answer: content.faq_multiple_answer,
    },
    {
      question: content.faq_timing_question,
      answer: content.faq_timing_answer,
    },
    {
      question: content.faq_promotions_question,
      answer: content.faq_promotions_answer,
    },
    {
      question: content.faq_bulk_question,
      answer: content.faq_bulk_answer,
    },
  ]

  return (
    <article>
      <PageMeta
        title={content.meta_title}
        description={content.meta_description}
        canonical={getCanonicalHref(ctx.theme.canonicalURL, '/repricer/')}
      />
      <SchemaMarkup schema={getFAQPageSchema(faqs)} id="faq-page-schema" />
      <Header />

      <Container className="!pt-12 !pb-20 sm:!pt-24 sm:!pb-32">
        <div className="max-w-4xl mx-auto text-center">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-6xl [font-variation-settings:'wdth'_113,'wght'_650] [text-wrap:balance]">
              {content.hero_title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff102e] to-[#fbca00] selection:text-black">
                {content.hero_marketplaces}
              </span>
            </h1>
            <p className="max-w-3xl mx-auto mt-6 text-lg leading-8 text-gray-600">
              {content.hero_description}
            </p>
            <Button as={Link} href="#access" theme="primary" className="mt-8 !px-6 !py-3 !text-base">
              {content.hero_cta}
            </Button>
          </div>
        </div>
      </Container>

      <Container className="!pt-0 !pb-16 sm:!pb-24">
        <h2 className="text-3xl font-medium text-gray-900">{content.trust_heading}</h2>
        <ul className="grid grid-cols-1 gap-6 mt-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: content.trust_scenarios_title,
              description: content.trust_scenarios_description,
            },
            {
              title: content.trust_minute_title,
              description: content.trust_minute_description,
            },
            {
              title: content.trust_rules_title,
              description: content.trust_rules_description,
            },
            {
              title: content.trust_ecosystem_title,
              description: content.trust_ecosystem_description,
            },
          ].map(({ title, description }) => (
            <li key={title} className="p-6 border border-primary-200 bg-primary-50 rounded-xl">
              <h3 className="text-lg font-medium text-gray-900 whitespace-pre-line xl:text-xl">{title}</h3>
              <p className="mt-3 text-base leading-7 text-primary-950/80">{description}</p>
            </li>
          ))}
        </ul>
      </Container>

      <Container className="!pt-0 !pb-20">
        <h2 className="text-3xl font-medium text-gray-900">{content.intro_heading}</h2>
        <p className="max-w-5xl mt-4 text-lg leading-8 text-gray-700">
          {content.intro_description}
        </p>
        <p className="max-w-5xl mt-3 text-lg leading-8 text-gray-700">
          {content.intro_explanation}
        </p>
        <ul className="grid grid-cols-1 gap-6 mt-8 lg:grid-cols-3">
          {[
            { icon: '⚡', title: content.intro_minute_title, description: content.intro_minute_description },
            { icon: '👀', title: content.intro_actual_price_title, description: content.intro_actual_price_description },
            { icon: '🤖', title: content.intro_assortment_title, description: content.intro_assortment_description },
          ].map(({ icon, title, description }) => (
            <li key={title} className="p-6 border border-primary-200 bg-primary-50 rounded-xl">
              <h3 className="text-xl font-medium text-gray-900">
                <span className="mr-3 text-2xl" aria-hidden="true">{icon}</span>{title}
              </h3>
              <p className="mt-3 text-base leading-7 text-primary-950/80">{description}</p>
            </li>
          ))}
        </ul>
        <ul className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-2">
          {[
            {
              icon: '🤖', title: content.needs_assortment_title,
              description: content.needs_assortment_description,
            },
            {
              icon: '📌', title: content.needs_rrp_title,
              description: content.needs_rrp_description,
            },
            {
              icon: '📣', title: content.needs_promotions_title,
              description: content.needs_promotions_description,
            },
            {
              icon: '💰', title: content.needs_margin_title,
              description: content.needs_margin_description,
            },
          ].map(({ icon, title, description }) => (
            <li key={title} className="p-6 border border-primary-200 bg-primary-50 rounded-xl">
              <h3 className="text-xl font-medium text-gray-900">
                <span className="mr-3 text-2xl" aria-hidden="true">{icon}</span>{title}
              </h3>
              <p className="mt-3 text-base leading-7 text-primary-950/80">{description}</p>
            </li>
          ))}
        </ul>
      </Container>

      <Container className="!bg-primary-50 !py-20">
        <h2 className="text-3xl font-medium text-gray-900">{content.scenarios_heading}</h2>
        <p className="max-w-5xl mt-4 text-lg leading-8 text-gray-700">
          {content.scenarios_description}
        </p>
        <ul className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-2">
          {[
            {
              icon: '💰', title: content.scenario_margin_title,
              description: content.scenario_margin_description,
              className: 'bg-lime-200 text-lime-950/90',
            },
            {
              icon: '📌', title: content.scenario_fixed_title,
              description: content.scenario_fixed_description,
              className: 'bg-indigo-100 text-indigo-950/90',
            },
            {
              icon: '⏰', title: content.scenario_schedule_title,
              description: content.scenario_schedule_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '🥊', title: content.scenario_competitors_title,
              description: content.scenario_competitors_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '🔗', title: content.scenario_cross_marketplace_title,
              description: content.scenario_cross_marketplace_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '📦', title: content.scenario_stock_title,
              description: content.scenario_stock_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '💎', title: content.scenario_rounding_title,
              description: content.scenario_rounding_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '📣', title: content.scenario_promotions_title,
              description: content.scenario_promotions_description,
              className: 'bg-white text-gray-700',
            },
            {
              icon: '⚙️', title: content.bulk_heading,
              description: content.bulk_description,
              className: 'bg-white text-gray-700 md:col-span-2',
            },
          ].map(({ icon, title, description, className }) => (
            <li key={title} className={c`p-6 rounded-xl ${className}`}>
              <h3 className="text-xl font-medium text-gray-900">
                <span className="mr-3 text-2xl" aria-hidden="true">{icon}</span>{title}
              </h3>
              <p className="mt-3 text-base leading-7">{description}</p>
            </li>
          ))}
        </ul>
      </Container>

      <Container className="!pt-20 !pb-0 sm:!pt-32">
        <h2 className="text-3xl font-medium text-gray-900">
          {content.speed_heading}
        </h2>
        <p className="max-w-5xl mt-4 text-lg leading-8 text-gray-700">
          {content.speed_description}
        </p>
        <ul className="grid grid-cols-1 gap-6 mt-8 sm:grid-cols-2">
          {[
            {
              icon: '📣', title: content.speed_discounts_title,
              description: content.speed_discounts_description,
            },
            {
              icon: '🚚', title: content.speed_stock_title,
              description: content.speed_stock_description,
            },
            {
              icon: '🔎', title: content.speed_competitors_title,
              description: content.speed_competitors_description,
            },
            {
              icon: '🚀', title: content.speed_settings_title,
              description: content.speed_settings_description,
            },
          ].map(({ icon, title, description }) => (
            <li key={title} className="p-6 border border-primary-200 bg-primary-50 rounded-xl">
              <h3 className="text-xl font-medium text-gray-900">
                <span className="mr-3 text-2xl" aria-hidden="true">{icon}</span>{title}
              </h3>
              <p className="mt-3 text-base leading-7 text-primary-950/80">{description}</p>
            </li>
          ))}
        </ul>
      </Container>

      <Container className="!py-20 sm:!py-32">
        <h2 className="text-3xl font-medium text-gray-900">{content.protection_heading}</h2>
        <p className="max-w-5xl mt-4 text-lg leading-8 text-gray-700">
          {content.protection_description}
        </p>
        <ul className="grid grid-cols-1 gap-6 mt-8 lg:grid-cols-3">
          {[
            {
              title: content.protection_settings_title,
              items: [
                { icon: '🔒', title: content.protection_minimum_title, description: content.protection_minimum_description },
                { icon: '💰', title: content.protection_margin_title, description: content.protection_margin_description },
                { icon: '🪂', title: content.protection_threshold_title, description: content.protection_threshold_description },
                { icon: '✅', title: content.protection_validation_title, description: content.protection_validation_description },
              ],
            },
            {
              title: content.protection_marketplace_title,
              items: [
                { icon: '🔍', title: content.protection_storefront_title, description: content.protection_storefront_description },
                { icon: '🧮', title: content.protection_logic_title, description: content.protection_logic_description },
                { icon: '🚫', title: content.protection_data_title, description: content.protection_data_description },
              ],
            },
            {
              title: content.protection_calculation_title,
              items: [
                { icon: '🔁', title: content.protection_steps_title, description: content.protection_steps_description },
                { icon: '🏁', title: content.protection_final_title, description: content.protection_final_description },
              ],
            },
          ].map(({ title, items }, index) => (
            <li key={title} className="p-6 border border-emerald-200 bg-emerald-50 rounded-xl">
              <h3 className="flex items-start gap-3 text-xl font-medium text-gray-900">
                <span className="flex items-center justify-center mt-1 text-sm text-white bg-emerald-600 rounded-full size-6 shrink-0">{index + 1}</span>
                {title}
              </h3>
              <ul className="mt-6 space-y-5">
                {items.map(({ icon, title, description }) => (
                  <li key={title}>
                    <h4 className="font-medium text-emerald-950"><span className="mr-2" aria-hidden="true">{icon}</span>{title}</h4>
                    <p className="mt-1 text-base leading-7 text-emerald-950/80">{description}</p>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <p className="max-w-5xl mt-6 text-lg leading-8 text-gray-700">
          {content.protection_conclusion}
        </p>
      </Container>

      <Faq title={content.faq_heading} faqs={faqs} className="!pt-0 !pb-12" />
      <DemoContactForm
        source="repricer"
        heading={content.demo_heading}
        description={content.demo_description}
      />
    </article>
  )
}

export default RepricerLandingPage
